import React, {useEffect} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {themeForType, brandForType} from './styles/theme';
import {BrowserRouter as Router} from 'react-router-dom';
import ProjectRoutes from './routes/index';
import {styled} from '@mui/system';
import {Toaster} from 'react-hot-toast';
import './App.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/es';
import 'moment/locale/sv';
import 'moment/locale/es-mx';
import useTranslations from './hooks/useTranslation';
import {GlobalStyles, Typography} from '@mui/material';
import {CONSTANTS} from './constants';
import useAuthStore from './store/authStore';
import {shallow} from 'zustand/shallow';
import {getOrganizationsApi, superAdminExitMasqueradeApi} from './api';
import Modal from './components/common/Modal';
import AcceptMoveModal from './components/common/AcceptMoveModal';

const isOrgLoginActive = org => Number(org?.deactivate_login) !== 1;

const initialLang = localStorage.getItem('selectedLang') || 'en';
moment.locale(CONSTANTS.MOMENTLOCALEMAP[initialLang] || 'en');

const AppContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'token' && prop !== 'role'
})(({theme, token, role}) => ({
  textAlign: 'center',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: token && (role === 'ADMIN' || role === 'SUPERADMIN') ? 'flex-start' : 'center',
  ...theme.palette.backgroundGradient
}));

function App() {
  const {loadAllTranslations, translate} = useTranslations();
  const {
    user,
    theme: themeType,
    selectedOrganization,
    token,
    setOrganization,
    logoutUser,
    orgLoginDisabledModal,
    clearOrgLoginDisabledModal,
    isSuperAdminMasquerading
  } = useAuthStore(
    state => ({
      user: state.user,
      theme: state.theme,
      selectedOrganization: state.selectedOrganization,
      token: state.token,
      setOrganization: state.setOrganization,
      logoutUser: state.logoutUser,
      orgLoginDisabledModal: state.orgLoginDisabledModal,
      clearOrgLoginDisabledModal: state.clearOrgLoginDisabledModal,
      isSuperAdminMasquerading: state.isSuperAdminMasquerading
    }),
    shallow
  );

  const role = user?.user_role;
  const selectedOrg = selectedOrganization;
  const selectedTheme = themeType ? Number(themeType) : 1;
  const preferredLanguage = user?.user_prefered_lang;

  // Theme selection is data-driven via themeForType/brandForType (see styles/theme.js) rather
  // than a switch over three hardcoded theme objects, so a new white-label brand needs no edit
  // here or in the GlobalStyles body rule below.

  useEffect(() => {
    const isMoved = selectedOrganization?.user_org_status?.toUpperCase() === 'MOVED';
    if (isSuperAdminMasquerading || isMoved) {
      document.body.classList.add('admin-masquerade-readonly');
    } else {
      document.body.classList.remove('admin-masquerade-readonly');
    }
    return () => document.body.classList.remove('admin-masquerade-readonly');
  }, [isSuperAdminMasquerading, selectedOrganization]);

  useEffect(() => {
    let languageCode = 'en';
    let dir = 'ltr';

    // Helper to detect matched device/browser language
    const getDeviceLangKey = () => {
      const deviceLangs = navigator.languages || [navigator.language || 'en'];
      for (let dl of deviceLangs) {
        const l = dl.toLowerCase();
        if (l.startsWith('es-mx')) return 5;
        if (l.startsWith('en-gb')) return 4;
        if (l.startsWith('sv')) return 3;
        if (l.startsWith('es')) return 2;
        if (l.startsWith('en')) return 1;
      }
      return null;
    };

    const deviceKey = getDeviceLangKey();

    // Priority Order: User Preference -> Matched Device Language -> Org Default Language -> English (1)
    const effectiveLangKey = preferredLanguage 
      || deviceKey 
      || selectedOrg?.default_language 
      || 1;

    const lang = CONSTANTS.LANG[effectiveLangKey];
    languageCode = typeof lang === 'object' ? lang.value : lang || 'en';
    // Direction is data on CONSTANTS.LANG (see useTranslation.switchLanguage) rather than a
    // hardcoded language check, so a new RTL language needs no edit here.
    dir = (typeof lang === 'object' && lang.dir) || 'ltr';

    const storedLang = localStorage.getItem('selectedLang');
    document.documentElement.setAttribute('lang', languageCode);
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('translate', 'no');
    document.documentElement.classList.add('notranslate');

    localStorage.setItem('selectedLang', languageCode);
    localStorage.setItem('direction', dir);
    moment.locale(CONSTANTS.MOMENTLOCALEMAP[languageCode] || 'en');

    const isDefaultLangSet = localStorage.getItem('isDefaultLangSet');
    // Reload translations if it's the first time or if the language code actually changed
    if (!isDefaultLangSet || storedLang !== languageCode) {
      loadAllTranslations(languageCode).then(() => {
        window.location.reload();
      });
      localStorage.setItem('isDefaultLangSet', 'true');
    }
  }, [selectedOrg, preferredLanguage]);

  useEffect(() => {
    const enforceOrganizationAccess = async () => {
      if (!token || !user || user?.user_role === 'SUPERADMIN') return;
      if (useAuthStore.getState().orgLoginDisabledModal) return;

      try {
        const response = await getOrganizationsApi(user?.id || '');
        if (response?.data?.status !== 200) return;
        const organizations = response?.data?.data?.updatedOrganizations || response?.data?.data || [];
        if (!Array.isArray(organizations) || organizations.length === 0) {
          logoutUser();
          window.location.replace('/login?orgDisabled=true');
          return;
        }

        const activeOrganization = organizations.find(org => isOrgLoginActive(org) && !['DELETED'].includes((org?.user_org_status || '').toUpperCase())) || organizations.find(isOrgLoginActive);
        const selectedOrgFromApi = organizations.find(org => org.id === selectedOrganization?.id);
        const isSelectedDisabled = Number(selectedOrgFromApi?.deactivate_login) === 1;

        const open = useAuthStore.getState().openOrgLoginDisabledModal;

        if (!activeOrganization) {
          open({
            action: 'logout',
            orgName: selectedOrgFromApi?.name || selectedOrganization?.name || organizations[0]?.name || ''
          });
          return;
        }

        if (!selectedOrganization || !selectedOrgFromApi || isSelectedDisabled) {
          open({
            action: 'switch_org',
            disabledOrgName: selectedOrgFromApi?.name || selectedOrganization?.name || '',
            activeOrganization,
            reloadAfterSwitch: false
          });
        }
      } catch (error) {}
    };
    enforceOrganizationAccess();
  }, [token, user?.id, user?.user_role, selectedOrganization?.id, logoutUser]);

  const handleOrgLoginDisabledOk = async () => {
    const block = useAuthStore.getState().orgLoginDisabledModal;
    if (!block) return;

    if (block.action === 'dismiss') {
      clearOrgLoginDisabledModal();
      return;
    }
    if (block.action === 'switch_org' && block.activeOrganization) {
      setOrganization(block.activeOrganization);
      if (block.reloadAfterSwitch) {
        window.location.reload();
      }
      clearOrgLoginDisabledModal();
      return;
    }
    if (useAuthStore.getState().isSuperAdminMasquerading) {
      try {
        const res = await superAdminExitMasqueradeApi();
        if (res?.data?.status === 200) {
          const { authToken, user: superAdminUser } = res.data.data;
          const returnUrl = localStorage.getItem('masqueradeReturnUrl') || '/super-admin/all-users';
          localStorage.removeItem('masqueradeReturnUrl');

          const authStorageStr = localStorage.getItem('auth-storage');
          if (authStorageStr) {
            try {
              const authStorage = JSON.parse(authStorageStr);
              if (authStorage.state) {
                authStorage.state.user = { ...superAdminUser };
                authStorage.state.token = authToken;
                authStorage.state.preferedTimezone = superAdminUser.prefered_timezone;
                authStorage.state.user_prefered_lang = superAdminUser.user_prefered_lang;
                authStorage.state.is_admin_login_mobile = superAdminUser.is_admin_login_mobile || false;
                authStorage.state.orgLoginDisabledModal = null;
                authStorage.state.isSuperAdminMasquerading = false;
                const preMasqueradeOrgStr = localStorage.getItem('preMasqueradeOrg');
                if (preMasqueradeOrgStr) {
                  try {
                    const preMasqueradeOrg = JSON.parse(preMasqueradeOrgStr);
                    if (preMasqueradeOrg) {
                      authStorage.state.selectedOrganization = preMasqueradeOrg;
                    }
                  } catch (e) {}
                  localStorage.removeItem('preMasqueradeOrg');
                }
                localStorage.setItem('auth-storage', JSON.stringify(authStorage));
              }
            } catch (e) {}
          }
          window.location.href = returnUrl;
          return;
        }
      } catch (error) {}
    }
    logoutUser();
    window.location.replace('/login');
    clearOrgLoginDisabledModal();
  };

  const modalBody = () => {
    const block = orgLoginDisabledModal;

    if (!block) return null;

    const disabledOrg = block.disabledOrgName || translate('This organization');
    const activeOrg = block.activeOrganization?.name || translate('an active organization');
    const orgName = block.orgName || translate('Your organization');

    switch (block.action) {
      case 'dismiss':
        return (
          <Typography sx={{color: 'black'}}>
            {translate('Please contact to the administrator to get access to the application.')}
          </Typography>
        );

      case 'switch_org':
        return (
          <Typography sx={{color: 'black'}}>
            {translate('{disabledOrg} has logins disabled. You will continue in {activeOrg}.')
              .replace('{disabledOrg}', disabledOrg)
              .replace('{activeOrg}', activeOrg)}
          </Typography>
        );

      default:
        return (
          <Typography sx={{color: 'black'}}>
            {translate('{orgName} has logins disabled and there is no other organization available. You will be signed out.').replace(
              '{orgName}',
              orgName
            )}
          </Typography>
        );
    }
  };

  return (
    <ThemeProvider theme={themeForType(selectedTheme)}>
      <GlobalStyles
        // styles={{
        //   body: {
        //     backgroundColor: themeType === 1 ? '#ca0000' : themeType === 2 ? '#66bb6a' : '#ffa726',
        //     backgroundImage: themeType === 1 ? 'linear-gradient(330deg, #ca0000 64%, #9e0001 58%)' : themeType === 2 ? 'linear-gradient(330deg, #4caf50 64%, #388e3c 58%)' : 'linear-gradient(330deg, #f57c00 64%, #ff9800 58%)'
        //   }
        // }}
        styles={{
          body: {
            // Data-driven from the org brand — the gradient (with the theme's
            // backgroundSize:cover) fills the body; backgroundColor is the fallback
            // beneath it. A new white-label brand needs no edit here.
            backgroundColor: brandForType(selectedTheme).bodyColor,
            backgroundImage: brandForType(selectedTheme).bodyGradient
          }
        }}
      />
      <Router>
        <AppContainer token={token} role={role}>
          <Modal
            open={Boolean(orgLoginDisabledModal)}
            setOpen={() => {}}
            onClose={() => {}}
            title={translate('Logins are temporarily disabled.')}
            modalbgColor="#ffffff"
            modalTextColor="#000000"
            modalTitleColor="#d32f2f"
            disableBackdropClose
            disableEscapeKeyDown
            backdropBlur
            buttons={[
              {
                name: translate('OK'),
                onClick: handleOrgLoginDisabledOk,
                buttonBgColor: '#d32f2f',
                buttonTextColor: '#ffffff',
                buttonHoverColor: '#b71c1c',
                borderRadius: '4px',
                ignoreMasqueradeDisable: true
              }
            ]}
          >
            {modalBody()}
          </Modal>

          <AcceptMoveModal />
          <ProjectRoutes />
          <Toaster
            position="bottom-center"
            gutter={-110}
            containerStyle={{
              bottom: '18%',
              pointerEvents: 'none'
            }}
            toastOptions={{
              success: {
                iconTheme: {
                  primary: 'black'
                }
              },
              style: {
                background: '#000',
                color: '#FFF',
                pointerEvents: 'none'
              }
            }}
          />
        </AppContainer>
      </Router>
    </ThemeProvider>
  );
}

export default App;
